import React, { useContext, useState, useEffect } from "react";
import OutputWindow from "../../components/ui/output_window/OutputWindow";
import styles from "./styles/Result.module.css";
import DataContext from "../../context/DataContext";
import { capitalizeFirstLetter, removeWhiteSpace } from "../../utils/utils";
import Receipt from "./Receipt";
import Spinner from "../../components/ui/spinner/Spinner";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import NavButton from "../../components/ui/navbutton/NavButton";
import { axiosSW, axiosSW2 } from "../../api/axiosDefaults";
import { useTranslation } from "react-i18next";
import { ErrorButtons } from "../../components/error_result_buttons/ErrorButtons";
import Button from "../../components/ui/button/Button";
import useAuthReady from "../../hooks/useAuthReady";

/**
 * The Result component
 * Used to diplay the result for searched ticket or processed tickets
 */

const Result = () => {
    const {
        currentCustom, //{Object} the client customizations
        result, //{Object} the result object
        isLoading, //{Boolean} the loading state
        handleError, //{Function} the error handler
        searchedTicket, //{String} the searched ticket number
        connection, //{Object} the client settings
        currentBU, //{String} the current business user
        reCaptchaVerification, //{Function} the reCaptcha verification function
        isServiceDataAvailable, //{Boolean} the service data availability state
        serviceData, //{Function} the service data fetcher
        homePath, //{String} the home path
        paymentHeaderFooterHeight, //{Number} the header and footer calculated height
        isServerDown,
        responseData,

        setIsValidSearch,
        setCurrentTicket,
        setTicketType,
        setCurrentCategory,

        setIsLoading,
        isrelatedRedirect,
        setIsrelatedRedirect,
        JWTToken,
    } = useContext(DataContext);
    const [receiptExist, setReceiptExist] = useState(false);
    const [receiptContent, setReceiptContent] = useState(null);
    const [relatedExist, setRelatedExist] = useState(false);

    const [isMinorError, setIsMinorError] = useState(false);
    const [inProcess, setInProcess] = useState(false);
    const [last4, setLast4] = useState("");
    const [formButtonDisable, setFormButtonDisable] = useState(true);
    const [notReadyView, setNotReadyView] = useState(true);
    const [isReceiptDowmloaded, setIsReceiptDowmloaded] = useState(false);
    const navigate = useNavigate();
    const { t } = useTranslation();
    const { bUser } = useParams();

    // Redirect to main page if no results. The page is not aceccable unless there is aresult object
    useEffect(() => {
        !result && navigate(`/${bUser}`);
    }, [bUser]);

    // Check if there is a last4CreditCardDigits proporty in the result object and invoke checkReceipt function
    useEffect(() => {
        const checkFunction = async () => {
            if (result?.last4CreditCardDigits && !isServerDown) {
                const result = await checkReceipt();
                setReceiptExist(result);
            }
        };
        checkFunction();
    }, []);

    // Check if there are relayed tickets or settlements
    useEffect(() => {
        if (
            responseData?.outputStatus === 2 ||
            responseData?.outputStatus === 3 ||
            responseData?.outputStatus === 5 ||
            responseData?.outputStatus === 6
            // || responseData?.outputStatus === 9 //temporary disabled backend issues
        ) {
            setRelatedExist(
                responseData?.relatedTickets?.length ||
                    responseData?.settlements?.length,
            );
        } else setRelatedExist(false);
    }, []);

    // Fetch the service data if not available or the business user has changed
    useEffect(() => {
        bUser &&
            JWTToken &&
            (!isServiceDataAvailable || bUser !== currentBU) &&
            !isServerDown &&
            serviceData(bUser);
    }, [bUser, isServiceDataAvailable, JWTToken]);

    useEffect(() => {
        if (result?.error?.message === "Failed reCAPTCHA verification") {
            setIsMinorError(true);
        }
    }, [result?.error?.message]);

    // Disable form button till the correct 4 digits input
    useEffect(() => {
        setFormButtonDisable(
            last4 &&
                last4.length === 4 &&
                last4 === result?.last4CreditCardDigits
                ? false
                : true,
        );
    }, [last4]);

    const redirectToRelated = () => {
        if (!relatedExist) return;
        setIsrelatedRedirect(true);
        if (responseData?.settlements?.length) {
            setIsValidSearch(true);
            setTicketType(1);
            setCurrentTicket(responseData?.settlements?.[0]);

            setCurrentCategory(
                responseData?.relatedTickets?.[0]?.photos?.[0]?.category,
            );
            navigate(`${homePath}/search`);
        } else if (responseData?.relatedTickets?.length) {
            setIsValidSearch(true);
            setCurrentTicket(responseData?.relatedTickets?.[0]);
            setTicketType(0);
            setCurrentCategory(
                responseData?.relatedTickets?.[0]?.photos?.[0]?.category,
            );
            navigate(`${homePath}/search`);
        }
    };

    /**
     * Check if there is a receipt available
     * @returns {Boolean} the receipt availability status
     */
    const checkReceipt = async () => {
        try {
            const { data } = await axiosSW2.post("/payments/receipt/exists", {
                customerId: connection?.uniqueId,
                paymentIntentID: result?.paymentIntentID,
                // ticketNo: searchedTicket,
                // last4CardDigits: result?.last4CreditCardDigits,
            });

            return data?.exists;
        } catch (error) {
            handleError(error);
        }
    };
    /**
     * Reset the last 4 digits input field
     */
    const resetLast4Input = () => {
        setLast4("");
    };

    /**
     * Fetch the receipt and download it
     */
    const getReceipt = async () => {
        try {
            setInProcess(true);
            const { data } = await axiosSW2.post("/payments/receipt", {
                customerId: connection?.uniqueId,
                paymentIntentID: result?.paymentIntentID,
                // ticketNo: searchedTicket,
                last4CardDigits: result?.last4CreditCardDigits,
            });

            const base64PDF = data.fileContent;

            const binaryString = window.atob(base64PDF);
            const len = binaryString.length;
            const bytes = new Uint8Array(len);
            for (let i = 0; i < len; i++) {
                bytes[i] = binaryString.charCodeAt(i);
            }

            const blob = new Blob([bytes], { type: "application/pdf" });

            const url = window.URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute("download", "receipt.pdf");

            document.body.appendChild(link);
            link.click();

            link.parentNode.removeChild(link);
            resetLast4Input();
            setIsReceiptDowmloaded(true);
            setInProcess(false);
        } catch (error) {
            handleError(error);
        }
    };

    /**
     * Create the email content object
     * @returns {Object} the email content object
     */
    const getEmailContent = () => {
        if (result?.error) {
            return {
                email: currentCustom?.supportEmail,
                subject: result?.errorMessage
                    ? result?.errorMessage
                    : "Unknown Error",
                body: result?.error,
            };
        }
    };

    /**
     * Create the result feed back window content
     * @returns {Object} the window content object
     */
    const getWindowContent = () => {
        let header = "";
        let message = "";

        if (result) {
            if (result?.error) {
                if (
                    result?.error?.message === "Failed reCAPTCHA verification"
                ) {
                    header = t(`reCaptcha error`);
                    message = t(`please try again later.`);
                } else if (
                    result?.error?.code === "Network Error" ||
                    result?.error?.request?.status === 0
                ) {
                    header = t(`server down`);

                    message = t(`please try again later.`);
                } else if (
                    responseData?.error?.response?.data?.outputStatus === 8 ||
                    responseData?.error?.outputStatus === 8
                ) {
                    header = t(`unhandled request`);
                    message = t(`unhandled request message`);
                } else if (
                    responseData?.error?.response?.data?.outputStatus === 0 ||
                    responseData?.error?.outputStatus === 0
                ) {
                    header = t(`unknown error`);
                    message = t(`unknown error message`);
                } else {
                    header = t(`technical error`)
                        ? t(`technical error`)
                        : "Technical error";
                    message = t(`technical error message`)
                        ? t(`technical error message`)
                        : "Please try again later.";
                }
            } else if (result?.outputStatus === 1) {
                header = t(`congratulation`);
                message = t(`congratulation message`);
            } else if (responseData?.outputStatus === 2) {
                header = t(`ticket reserved`);
                message = t(`ticket reserved message`);
            } else if (responseData?.outputStatus === 5) {
                header = t(`settlement reserved`);
                message = t(`settlement reserved message`);
            } else if (responseData?.outputStatus === 9) {
                header = t(`ticket in process`);
                message = t(`ticket in process message`);
            } else if (!result?.paidNow && responseData?.outputStatus === 3) {
                header = t(`ticket paid`);
                message = receiptExist
                    ? isReceiptDowmloaded
                        ? t(`receipt downloaded message`)
                        : t(`ticket paid receipt message`)
                    : t(`ticket paid message`);
            } else if (!result?.paidNow && responseData?.outputStatus === 6) {
                header = t(`settlement paid`);
                message = receiptExist
                    ? isReceiptDowmloaded
                        ? t(`receipt downloaded message`)
                        : t(`settlement paid receipt message`)
                    : t(`settlement paid message`);
            } else if (result?.paidNow && result?.paymentIntentID) {
                header = t(`payment was successful`);
                message = receiptExist
                    ? isReceiptDowmloaded
                        ? t(`receipt downloaded message`)
                        : t(`successful paid receipt message`)
                    : t(`successful paid message`);
            } else {
                header = t(`unhandled request or error`);
                message = t(`unhandled request or error message`);
            }

            return (
                <>
                    {inProcess ? (
                        <div
                            className={`${styles.result_spinner_container} flex-container`}
                        >
                            <Spinner />
                        </div>
                    ) : (
                        <div
                            className={`${styles.result_text_container} v-flex-container`}
                        >
                            <h1>{header}</h1>

                            <p>{capitalizeFirstLetter(message)}</p>
                        </div>
                    )}
                </>
            );
        }
    };

    /**
     * Submit handle
     * @param {Event} event - the event object
     */
    const handleSubmit = (event) => {
        event.preventDefault();
        if (!formButtonDisable && !inProcess) {
            getReceipt();
            // downloadRecipt();
        }
    };

    /**
     * Change handle
     * @param {Event} event - the event object
     */
    const handleChange = (event) => {
        event.preventDefault();

        const inputValue = event.target.value;
        const lastChar = inputValue.slice(-1);
        !isNaN(lastChar) && setLast4(removeWhiteSpace(inputValue));
    };

    useEffect(() => {
        if (isServiceDataAvailable || !isLoading) {
            const timer = setTimeout(() => {
                setNotReadyView(false);
            }, 50);

            // Cleanup function
            return () => {
                clearTimeout(timer);
            };
        }
    }, [isServiceDataAvailable, isLoading]);

    return (
        <article
            className={`  main-page-container flex-container`}
            style={{
                minHeight: `calc(100vh - ${paymentHeaderFooterHeight}px)`,
            }}
        >
            {isLoading || (!isServerDown && !isServiceDataAvailable) ? (
                <Spinner />
            ) : (
                <section
                    className={` ${
                        styles.main_result_page_container
                    } transition-container  ${
                        notReadyView
                            ? "transition-hidden"
                            : "transition-visible"
                    }  main-container flex-container`}
                >
                    <div
                        className={`${styles.result_main_container} v-flex-container`}
                    >
                        <OutputWindow
                            color={
                                !result?.error
                                    ? currentCustom?.color?.current
                                    : currentCustom?.color?.error
                            }
                            content={getWindowContent()}
                        />

                        {result?.error ? (
                            <ErrorButtons
                                isMinorError={isMinorError}
                                // emailData={getEmailContent()}
                            />
                        ) : (
                            <>
                                {receiptExist && !isReceiptDowmloaded ? (
                                    <Receipt
                                        handleChange={handleChange}
                                        handleSubmit={handleSubmit}
                                        last4={last4}
                                        formButtonDisable={formButtonDisable}
                                        inProcess={inProcess}
                                    />
                                ) : (
                                    <></>
                                )}
                                <div
                                    className={`${styles.result_buttons_container} flex-container`}
                                >
                                    <div className={`flex-container`}>
                                        <NavButton
                                            body={t(`back to search`)}
                                            link={homePath}
                                        />
                                    </div>
                                    {relatedExist && !isrelatedRedirect ? (
                                        <div className={`flex-container`}>
                                            <Button
                                                body={t(`related entries`)}
                                                type={"button"}
                                                onClickFuction={
                                                    redirectToRelated
                                                }
                                            />
                                        </div>
                                    ) : (
                                        <></>
                                    )}
                                </div>
                            </>
                        )}
                    </div>
                </section>
            )}
        </article>
    );
};

export default Result;
